// @intent: Provider-neutral contracts for the F8.13 visual/mechanic semantic QA gate.
// @flow: programmatic checks + judge verdict -> aggregated VisualMechanicReportV1 -> generation verify gate + QA harness.
// @stale: keep literals stable; persisted in generation_run_artifacts payloads and eval reports.

import { z } from 'zod/v4';

export const MechanicFamilySchema = z.enum(['maze', 'runner', 'puzzle', 'match', 'collect', 'shooter', 'slot', 'merge', 'tower', 'draw', 'generic']);
export type MechanicFamily = z.infer<typeof MechanicFamilySchema>;

export const VisualMechanicSeveritySchema = z.enum(['blocking', 'advisory']);

export const ProgrammaticCheckResultV1Schema = z.strictObject({
  id: z.string().trim().min(1),
  status: z.enum(['passed', 'failed', 'skipped']),
  severity: VisualMechanicSeveritySchema,
  reason: z.string().trim().min(1),
  evidence: z.record(z.string(), z.unknown()).default({}),
});
export type ProgrammaticCheckResultV1 = z.infer<typeof ProgrammaticCheckResultV1Schema>;

export const VisualMechanicScoresV1Schema = z.strictObject({
  mechanicEmbodiment: z.number().min(0).max(5),
  visualCoherence: z.number().min(0).max(5),
  assetRoleSpecificity: z.number().min(0).max(5),
  playfieldClarity: z.number().min(0).max(5),
  arcAndPayoff: z.number().min(0).max(5),
  ctaIntegration: z.number().min(0).max(5),
  // @intent animationJuice (0-5): is the motion eased/tweened, scale-pop/squash-stretch on interactions, with a
  //  satisfying endcard reveal — i.e. does it feel JUICY ("красиво, сочно"), not static/instant snaps? ADVISORY only.
  animationJuice: z.number().min(0).max(5),
});

export const VisualMechanicFailureV1Schema = z.strictObject({
  gate: z.string().trim().min(1),
  reason: z.string().trim().min(1),
  evidence: z.string().trim().min(1),
  repairInstruction: z.string().trim().min(1),
});

export const VisualMechanicJudgeVerdictV1Schema = z.strictObject({
  version: z.literal('v1'),
  pass: z.boolean(),
  severity: VisualMechanicSeveritySchema,
  inconclusive: z.boolean().default(false),
  scores: VisualMechanicScoresV1Schema,
  failures: z.array(VisualMechanicFailureV1Schema).default([]),
});
export type VisualMechanicJudgeVerdictV1 = z.infer<typeof VisualMechanicJudgeVerdictV1Schema>;

/** @intent Conditional judge gating mode: off skips the judge; advisory records but never blocks; blocking fails closed. @flow VISUAL_MECHANIC_JUDGE_MODE env -> aggregate gating. @stale default is advisory until the live judge is wired. */
export const VisualMechanicJudgeModeSchema = z.enum(['off', 'advisory', 'blocking']);
export type VisualMechanicJudgeMode = z.infer<typeof VisualMechanicJudgeModeSchema>;

/** @intent Honest judge outcome: skipped_unconfigured/off are NOT pass; pass requires a real passing verdict. @flow resolveJudgeStatus. @stale never label a missing judge as pass. */
export const VisualMechanicJudgeStatusSchema = z.enum(['off', 'skipped_unconfigured', 'pass', 'fail', 'inconclusive', 'error']);
export type VisualMechanicJudgeStatus = z.infer<typeof VisualMechanicJudgeStatusSchema>;

export const VisualMechanicReportV1Schema = z.strictObject({
  version: z.literal('v1'),
  pass: z.boolean(),
  severity: VisualMechanicSeveritySchema,
  scores: VisualMechanicScoresV1Schema,
  programmaticChecks: z.array(ProgrammaticCheckResultV1Schema),
  failures: z.array(VisualMechanicFailureV1Schema),
  deliveryAllowed: z.boolean(),
  judgeConfigured: z.boolean(),
  judgeMode: VisualMechanicJudgeModeSchema,
  judgeStatus: VisualMechanicJudgeStatusSchema,
  judgeReason: z.string().nullable().default(null),
  evidence: z.record(z.string(), z.unknown()).default({}),
});
export type VisualMechanicReportV1 = z.infer<typeof VisualMechanicReportV1Schema>;
